import {
	Chart as ChartJS,
	Filler,
	Legend,
	LineElement,
	PointElement,
	RadialLinearScale,
	Tooltip,
} from 'chart.js';
import { debounce } from 'lodash';
import { rgba } from 'polished';
import { FC, useEffect, useState } from 'react';
import { Radar as RadarChart } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { FlexGap, Paragraph, palettes, tokens } from '@treams/treams-ui';

import { GrowthPathData } from '../GrowthPathConverter';
import { useGrowthPathJobCompetencyFilter } from '../hooks/useGrowthPathFilter';
import { wrapRadarPointLabel } from './radarPointLabel';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const expectedColor = palettes.vividPink['400'];
const currentColor = palettes.freshGreen['400'];
const expectedBorder = palettes.vividPink['600'];
const currentBorder = palettes.freshGreen['600'];

const Swatch = styled(Box)<{ $color: string; $border: string }>`
	width: 12px;
	height: 12px;
	flex-shrink: 0;
	border-radius: 3px;
	background: ${(props) => props.$color};
	border: 1px solid ${(props) => props.$border};
`;

type Props = {
	data: GrowthPathData;
	labelID: string | null;
};

const GrowthPathChart: FC<Props> = ({ data, labelID }) => {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const [resizeKey, setResizeKey] = useState(0);

	useEffect(() => {
		const handleResize = debounce(() => setResizeKey((k) => k + 1), 150);

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);

			handleResize.cancel();
		};
	}, []);

	const filteredJobCompetencies = useGrowthPathJobCompetencyFilter(labelID, data.jobCompetencies);

	const jobCompetenciesData = filteredJobCompetencies!.map(
		(jobCompetency) => jobCompetency.competencyLevel + 1,
	);
	const competenciesData = filteredJobCompetencies!.map((jobCompetency) => {
		const appraisal = jobCompetency.appraisal;
		if (appraisal) {
			return Math.round(appraisal.competencyLevelIndex) + 1;
		} else {
			return 0;
		}
	});

	const maxCompetencyLevel = data.jobCompetencies.reduce((max, competency) => {
		return Math.max(max, competency.competency.levels.length);
	}, 0);

	const chartData = {
		labels: filteredJobCompetencies!.map(
			(jobCompetency) => localize(jobCompetency.competency.title ?? []).text,
		),

		datasets: [
			{
				label: t('growthPath.chart.expected'),
				data: jobCompetenciesData,
				backgroundColor: rgba(expectedColor, 0.2),
				borderColor: expectedColor,
				borderWidth: 2,
			},
			{
				label: t('growthPath.chart.current'),
				data: competenciesData,
				backgroundColor: rgba(currentColor, 0.2),
				borderColor: currentColor,
				borderWidth: 2,
			},
		],
	};

	const options = {
		responsive: true,
		scales: {
			r: {
				beginAtZero: true,
				max: maxCompetencyLevel,
				ticks: { display: false, stepSize: 1 },
				grid: { color: tokens.border.subtle },
				angleLines: { color: tokens.border.subtle },
				pointLabels: {
					color: tokens.text.secondary,
					font: { family: tokens.typography.fontFamily, size: tokens.typography.sizes.small },
					callback(label: string, index: number) {
						return wrapRadarPointLabel(this as unknown as RadialLinearScale, label, index);
					},
				},
			},
		},
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: {
					label: (context) => {
						const value = context.raw;
						return `${context.dataset.label}: ${value === 0 ? t('growthPath.chart.noScore') : value}`;
					},
				},
			},
		},
	};

	return (
		<Box>
			<FlexGap
				mt={`${tokens.gap.group}px`}
				mb={`${tokens.gap.list}px`}
				gap={tokens.gap.list}
				alignItems="center"
			>
				<FlexGap alignItems="center" gap={tokens.gap.inline}>
					<Swatch $color={expectedColor} $border={expectedBorder} />
					<Paragraph small mb={0}>
						{t('growthPath.chart.expected')}
					</Paragraph>
				</FlexGap>
				<FlexGap alignItems="center" gap={tokens.gap.inline}>
					<Swatch $color={currentColor} $border={currentBorder} />
					<Paragraph small mb={0}>
						{t('growthPath.chart.current')}
					</Paragraph>
				</FlexGap>
			</FlexGap>
			<RadarChart key={resizeKey} data={chartData} options={options} />
		</Box>
	);
};
export default GrowthPathChart;
